import { useEffect, useRef, useState } from "react";
import { emailAdmin } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RichTextEditor } from "@/components/RichTextEditor";
import { MailCheck, Send, RotateCcw, Lock, ChevronDown, History, Loader2, Code, Clock, Play } from "lucide-react";
import { fmtLog } from "@/lib/tz";
import { useAuth } from "@/context/AuthContext";

const TEAL = "#0f766e";

const STATUS_STYLE = {
  sent: "bg-emerald-50 text-emerald-700 border-emerald-200",
  failed: "bg-rose-50 text-rose-700 border-rose-200",
  skipped: "bg-slate-100 text-slate-500 border-slate-200",
};

function Card({ t, onSaved }) {
  const { can } = useAuth();
  // Security emails (codes, reset and signing links): only Admins change the wording.
  const wordingLocked = t.security && !can.has("settings.security_emails");
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(t.enabled);
  const [subject, setSubject] = useState(t.subject);
  const [body, setBody] = useState(t.body);
  const [recipients, setRecipients] = useState(t.recipients || "");
  const [cc, setCc] = useState(t.cc || "");
  const [ccEnabled, setCcEnabled] = useState(t.cc_enabled !== false);
  const [thresholds, setThresholds] = useState((t.thresholds || []).join(", "));
  const [busy, setBusy] = useState("");
  const [htmlMode, setHtmlMode] = useState(false);
  const editorRef = useRef(null);

  const toggle = async (v) => {
    setEnabled(v);
    try { await emailAdmin.update(t.key, { enabled: v }); toast.success(v ? "Turned on" : "Turned off"); onSaved(); }
    catch (e) { setEnabled(!v); toast.error(e.response?.data?.detail || "Couldn't change this."); }
  };
  const save = async () => {
    setBusy("save");
    try {
      const payload = wordingLocked ? { recipients, cc } : { subject, body, recipients, cc };
      if (t.cc_toggle) payload.cc_enabled = ccEnabled;
      if (t.thresholds_schema) payload.thresholds = thresholds.split(",").map((x) => parseFloat(x.trim())).filter((n) => !isNaN(n));
      await emailAdmin.update(t.key, payload); toast.success("Saved"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setBusy(""); }
  };
  const reset = async () => {
    setBusy("reset");
    try { const d = await emailAdmin.reset(t.key); setSubject(d.subject); setBody(d.body); setThresholds((d.thresholds || []).join(", ")); toast.success("Reset to default"); onSaved(); }
    catch { toast.error("Reset failed"); } finally { setBusy(""); }
  };
  const test = async () => {
    setBusy("test");
    try { const d = await emailAdmin.test(t.key); toast.success(`Test sent to ${d.sent_to}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Test failed"); } finally { setBusy(""); }
  };
  const insertField = (f) => {
    const token = `{${f}}`;
    if (htmlMode || !editorRef.current) setBody((b) => `${b}${token}`);
    else editorRef.current.insertText(token);
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid={`email-card-${t.key}`}>
      <div className="flex items-center gap-3 p-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-800">{t.label}</span>
            {t.locked && <Badge className="border bg-slate-100 text-slate-500 border-slate-200"><Lock className="h-3 w-3 mr-1" />Always on</Badge>}
            <Badge className={`border ${enabled ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-400 border-slate-200"}`}>{enabled ? "On" : "Off"}</Badge>
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5">{t.description} <span className="text-slate-400">· to {t.audience}</span></p>
        </div>
        {!t.locked && <Switch checked={enabled} onCheckedChange={toggle} data-testid={`email-toggle-${t.key}`} />}
        <Button size="sm" variant="outline" className="h-8" onClick={() => setOpen((o) => !o)} data-testid={`email-edit-${t.key}`}>
          Edit <ChevronDown className={`h-4 w-4 ml-1 transition-transform ${open ? "rotate-180" : ""}`} />
        </Button>
      </div>
      {open && (
        <div className="border-t border-slate-100 p-4 space-y-3 bg-slate-50/50">
          <div>
            <Label className="text-[11px] font-semibold text-slate-600">Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} disabled={wordingLocked} className="h-9 mt-1 text-sm bg-white" data-testid={`email-subject-${t.key}`} />
          </div>
          {wordingLocked && (
            <p className="text-[11px] text-slate-500 flex items-center gap-1" data-testid={`email-wording-locked-${t.key}`}>
              <Lock className="h-3 w-3" />Security email: only an Admin can change its wording.
            </p>
          )}
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-[11px] font-semibold text-slate-600">Body</Label>
              <button type="button" onClick={() => setHtmlMode((m) => !m)} className="text-[10px] inline-flex items-center gap-1 text-slate-400 hover:text-slate-600" data-testid={`email-html-toggle-${t.key}`}>
                <Code className="h-3 w-3" />{htmlMode ? "Visual editor" : "View HTML source"}
              </button>
            </div>
            {wordingLocked ? (
              <Textarea value={body} readOnly rows={7} className="mt-1 text-sm font-mono bg-slate-50" data-testid={`email-body-${t.key}`} />
            ) : htmlMode ? (
              <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={7} className="mt-1 text-sm font-mono bg-white" data-testid={`email-body-${t.key}`} />
            ) : (
              <RichTextEditor ref={editorRef} value={body} onChange={setBody} headings links placeholder="Write the email…" testid={`email-body-${t.key}`} />
            )}
            {t.fields?.length > 0 && !wordingLocked && (
              <div className="flex flex-wrap gap-1 mt-2">
                <span className="text-[10px] text-slate-400 mr-1 self-center">Insert:</span>
                {t.fields.map((f) => (
                  <button key={f} onClick={() => insertField(f)} className="text-[10px] px-2 py-0.5 rounded-full border border-slate-300 bg-white text-slate-600 hover:bg-slate-100" data-testid={`email-field-${t.key}-${f}`}>{`{${f}}`}</button>
                ))}
              </div>
            )}
          </div>
          {t.recipients_editable && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label className="text-[11px] font-semibold text-slate-600">Send to (leave blank for default)</Label>
                <Input value={recipients} onChange={(e) => setRecipients(e.target.value)} placeholder="hr@prydessupport.com" className="h-9 mt-1 text-sm bg-white" data-testid={`email-to-${t.key}`} /></div>
              <div><Label className="text-[11px] font-semibold text-slate-600">CC (comma-separated)</Label>
                <Input value={cc} onChange={(e) => setCc(e.target.value)} className="h-9 mt-1 text-sm bg-white" data-testid={`email-cc-${t.key}`} /></div>
            </div>
          )}
          {t.cc_toggle && (
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label className="text-[11px] font-semibold text-slate-600">CC the office</Label>
                  <p className="text-[11px] text-slate-500 mt-0.5">Also send a copy to the office for escalation visibility.</p>
                </div>
                <Switch checked={ccEnabled} onCheckedChange={setCcEnabled} data-testid={`email-cc-toggle-${t.key}`} />
              </div>
              {ccEnabled && (
                <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="hr@prydessupport.com" className="h-9 mt-2 text-sm bg-white" data-testid={`email-cc-addr-${t.key}`} />
              )}
            </div>
          )}
          {t.thresholds_schema && (
            <div>
              <Label className="text-[11px] font-semibold text-slate-600">{t.thresholds_schema.label} ({t.thresholds_schema.unit})</Label>
              <Input value={thresholds} onChange={(e) => setThresholds(e.target.value)} placeholder="30, 14, 7" className="h-9 mt-1 text-sm bg-white" data-testid={`email-thresholds-${t.key}`} />
            </div>
          )}
          <div className="flex items-center gap-2 pt-1">
            <Button size="sm" style={{ backgroundColor: TEAL }} onClick={save} disabled={busy === "save"} data-testid={`email-save-${t.key}`}>{busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
            <Button size="sm" variant="outline" onClick={test} disabled={busy === "test"} data-testid={`email-test-${t.key}`}><Send className="h-3.5 w-3.5 mr-1" />Send test</Button>
            {!wordingLocked && <Button size="sm" variant="ghost" onClick={reset} disabled={busy === "reset"} data-testid={`email-reset-${t.key}`}><RotateCcw className="h-3.5 w-3.5 mr-1" />Reset</Button>}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scheduled jobs section
// ---------------------------------------------------------------------------
function fmt24(h, m) {
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function ScheduleRow({ job, onChange, onRunNow }) {
  const [running, setRunning] = useState(false);
  const handleTime = (val) => { const [h, m] = val.split(":").map(Number); if (!isNaN(h) && !isNaN(m)) onChange({ hour: h, minute: m }); };
  const handleInterval = (val) => { const n = parseInt(val, 10); if (!isNaN(n) && n >= 1) onChange({ interval_minutes: n }); };
  const runNow = async () => {
    setRunning(true);
    try { await onRunNow(job.name); toast.success(`${job.label} triggered`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not trigger job"); }
    finally { setRunning(false); }
  };
  return (
    <div className="flex items-center gap-3 py-3 px-4 border-b border-slate-100 last:border-0 flex-wrap" data-testid={`schedule-row-${job.name}`}>
      <Switch checked={job.enabled} onCheckedChange={(v) => onChange({ enabled: v })} disabled={job.locked} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-800">{job.label}{job.locked && <Badge className="ml-2 border bg-slate-100 text-slate-500 border-slate-200" data-testid={`schedule-locked-${job.name}`}>Admin only</Badge>}</p>
        <p className="text-xs text-slate-400">{job.description}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {job.mode === "daily" || job.mode === "weekly" ? (
          <div className="flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5 text-slate-400" />
            {job.mode === "weekly" && (
              <select value={job.weekday} onChange={(e) => onChange({ weekday: Number(e.target.value) })} disabled={!job.enabled || job.locked}
                className="h-8 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-teal-600 disabled:opacity-40"
                data-testid={`schedule-weekday-${job.name}`}>
                {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
              </select>
            )}
            <input type="time" value={fmt24(job.hour, job.minute)} onChange={(e) => handleTime(e.target.value)} disabled={!job.enabled || job.locked}
              className="h-8 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-teal-600 disabled:opacity-40"
              data-testid={`schedule-time-${job.name}`} />
            <span className="text-xs text-slate-400">org time · {job.mode === "weekly" ? "weekly" : "daily"}</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5 text-slate-400" />
            <input type="number" min="1" max="1440" value={job.interval_minutes} onChange={(e) => handleInterval(e.target.value)} disabled={!job.enabled || job.locked}
              className="h-8 w-20 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-teal-600 disabled:opacity-40"
              data-testid={`schedule-interval-${job.name}`} />
            <span className="text-xs text-slate-400">min interval</span>
          </div>
        )}
        <Button size="sm" variant="outline" onClick={runNow} disabled={running} title="Run now" data-testid={`schedule-run-${job.name}`}>
          {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
        </Button>
      </div>
    </div>
  );
}

function ScheduleSection() {
  const [jobs, setJobs] = useState(null);
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { emailAdmin.getCronSchedule().then((d) => setJobs(d.jobs)).catch(() => {}); }, []);
  const handleChange = (name, patch) => {
    setJobs((prev) => prev.map((j) => j.name === name ? { ...j, ...patch } : j));
    setDirty((d) => ({ ...d, [name]: true }));
  };
  const save = async () => {
    setSaving(true);
    try {
      const payload = jobs.map((j) => {
        const e = { name: j.name, enabled: j.enabled };
        if (j.mode === "daily" || j.mode === "weekly") { e.hour = j.hour; e.minute = j.minute; }
        if (j.mode === "weekly") e.weekday = j.weekday;
        else { e.interval_minutes = j.interval_minutes; }
        return e;
      });
      const d = await emailAdmin.saveCronSchedule(payload);
      setJobs(d.jobs); setDirty({}); toast.success("Schedule saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  if (!jobs) return <div className="p-4 text-xs text-slate-400">Loading schedule…</div>;
  return (
    <div className="rounded-xl border border-slate-200 bg-white mb-8" data-testid="schedule-section">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-teal-700" />
          <span className="text-sm font-semibold text-slate-800">Scheduled Jobs</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">Times are in your organisation's timezone</span>
          {Object.keys(dirty).length > 0 && (
            <Button size="sm" style={{ backgroundColor: TEAL }} onClick={save} disabled={saving} data-testid="schedule-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save schedule"}
            </Button>
          )}
        </div>
      </div>
      {jobs.map((job) => (
        <ScheduleRow key={job.name} job={job} onChange={(patch) => handleChange(job.name, patch)} onRunNow={emailAdmin.runJobNow} />
      ))}
    </div>
  );
}

export default function ReminderEmails() {
  const [data, setData] = useState(null);
  const [showLog, setShowLog] = useState(false);
  const [log, setLog] = useState([]);
  const load = () => emailAdmin.list().then(setData).catch(() => {});
  useEffect(() => { load(); }, []);
  const openLog = async () => { setShowLog((s) => !s); if (!showLog) { try { const d = await emailAdmin.log(); setLog(d.log || []); } catch { /* noop */ } } };

  if (!data) return <div className="p-8 text-sm text-slate-400">Loading…</div>;
  return (
    <div className="max-w-4xl mx-auto p-6" data-testid="reminder-emails-page">
      <div className="flex items-center gap-2 mb-1">
        <MailCheck className="h-5 w-5" style={{ color: TEAL }} />
        <h1 className="text-xl font-semibold font-brand text-slate-800">Reminder Emails</h1>
      </div>
      <p className="text-sm text-slate-500 mb-5">Turn each automated email on or off, edit its wording, choose who receives it, set reminder thresholds, and send yourself a test. Emails go through your own SMTP sender.</p>
      <ScheduleSection />
      <Button size="sm" variant="outline" className="mb-4" onClick={openLog} data-testid="email-log-toggle"><History className="h-4 w-4 mr-1.5" />{showLog ? "Hide" : "View"} send log</Button>
      {showLog && (
        <div className="rounded-xl border border-slate-200 bg-white mb-6 overflow-hidden" data-testid="email-log">
          {log.length === 0 ? <p className="text-xs text-slate-400 p-4">No emails sent yet.</p> : (
            <table className="w-full text-xs">
              <thead><tr className="text-left text-[10px] uppercase text-slate-400 border-b"><th className="p-2">When</th><th className="p-2">Email</th><th className="p-2">To</th><th className="p-2">Status</th></tr></thead>
              <tbody>{log.map((r, i) => (
                <tr key={i} className="border-b border-slate-50"><td className="p-2 text-slate-500 whitespace-nowrap">{fmtLog(r.at)}</td><td className="p-2">{r.label}</td><td className="p-2 text-slate-500">{r.to}</td><td className="p-2"><Badge className={`border ${STATUS_STYLE[r.status] || ""}`}>{r.status}</Badge>{r.status === "failed" && <span className="text-[10px] text-rose-500 ml-1">{r.detail}</span>}</td></tr>
              ))}</tbody>
            </table>
          )}
        </div>
      )}
      {data.categories.map((cat) => {
        const items = data.templates.filter((t) => t.category === cat);
        if (!items.length) return null;
        return (
          <div key={cat} className="mb-6">
            <h2 className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">{cat}</h2>
            <div className="space-y-2">{items.map((t) => <Card key={t.key} t={t} onSaved={load} />)}</div>
          </div>
        );
      })}
    </div>
  );
}
